import { useCallback, useEffect, type ReactNode, type ChangeEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { useSession } from '@/hooks/useSession';
import { useQariMutations, useQariProfile } from '@/hooks/useQariProfile';
import type { AutosaveStatus } from '@/hooks/useDebouncedAutosave';
import {
  QARI_EDITABLE_STATUSES,
  type QariProfile,
} from '@/types/qari';
import { IconCheck, IconChevronRight } from '@/components/home/icons';

/**
 * Qari onboarding — shared wizard shell.
 *
 * Each step of the qari onboarding flow lives on its own route
 * (/qari/onboarding/identity, /expertise, ...). They all share this chrome:
 *
 *   - Exit-Flow header
 *   - 3-step progress bar + step markers
 *   - Glass form container with heading/subtitle slot for the step
 *   - Action bar with Prev/Next + autosave indicator
 *
 * The bootstrap-and-status-guard hook (`useQariDraftProfile`) and the
 * form-field primitives are exported from this file too so each step
 * page can stay focused on its own fields + backend mapping.
 */

/* ================================================================== *
 * Step definitions
 * ================================================================== */

export const STEP_DEFS = [
  { n: 1, label: 'Profile' },
  { n: 2, label: 'Expertise' },
  { n: 3, label: 'Verification' },
] as const;

export const TOTAL_STEPS = STEP_DEFS.length;

/* ================================================================== *
 * Hook: bootstrap empty draft + status guard
 * ================================================================== */

export type QariDraftState =
  | { phase: 'loading' }
  | { phase: 'error'; title: string; detail: string; retry: () => void }
  | { phase: 'redirect' }
  | { phase: 'ready'; profile: QariProfile; userId: string };

/**
 * Returns a discriminated union the caller can switch on. On first visit
 * (no profile row yet), it kicks off `saveDraft.mutate({})` so the wizard
 * always has a profile to bind to. If the profile is already past
 * `draft`/`rejected`, it signals a redirect to `/qari`.
 */
export function useQariDraftProfile(): QariDraftState {
  const session = useSession();
  const profile = useQariProfile();
  const { saveDraft } = useQariMutations();
  const userId = session.data?.user.id ?? null;

  useEffect(() => {
    if (
      session.data &&
      profile.isSuccess &&
      !profile.data &&
      !saveDraft.isPending &&
      !saveDraft.isError
    ) {
      saveDraft.mutate({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data?.user.id, profile.isSuccess, profile.data]);

  if (profile.isLoading) return { phase: 'loading' };

  if (profile.isError) {
    return {
      phase: 'error',
      title: "Couldn't load your profile",
      detail:
        profile.error instanceof Error
          ? profile.error.message
          : 'Unknown error.',
      retry: () => profile.refetch(),
    };
  }

  if (!profile.data) {
    if (saveDraft.isError) {
      return {
        phase: 'error',
        title: "Couldn't start your draft",
        detail:
          saveDraft.error instanceof Error
            ? saveDraft.error.message
            : 'Unknown error.',
        retry: () => saveDraft.mutate({}),
      };
    }
    return { phase: 'loading' };
  }

  if (!QARI_EDITABLE_STATUSES.has(profile.data.status)) {
    return { phase: 'redirect' };
  }

  return { phase: 'ready', profile: profile.data, userId: userId! };
}

/* ================================================================== *
 * WizardShell — the chrome around each step
 * ================================================================== */

export type WizardNav =
  | { kind: 'link'; label: string; to: string }
  | {
      kind: 'action';
      label: string;
      onClick: () => void | Promise<void>;
      pending?: boolean;
      disabled?: boolean;
    }
  | null;

export type WizardShellProps = {
  currentStep: number;
  title: string;
  heading: string;
  subtitle: string;
  prev: WizardNav;
  next: WizardNav;
  /** When present, flush() is awaited before any nav action fires. */
  autosave?: { status: AutosaveStatus; flush: () => Promise<void> };
  /** Inline note rendered above the action bar (e.g. "Step 3 coming next"). */
  hint?: ReactNode;
  /** Rendered after the form container (e.g. supportive info cards). */
  extras?: ReactNode;
  children: ReactNode;
};

export function WizardShell({
  currentStep,
  title,
  heading,
  subtitle,
  prev,
  next,
  autosave,
  hint,
  extras,
  children,
}: WizardShellProps) {
  const navigate = useNavigate();
  const progressPct = (currentStep / TOTAL_STEPS) * 100;

  const handleNav = useCallback(
    async (nav: WizardNav) => {
      if (!nav) return;
      if (nav.kind === 'action' && (nav.disabled || nav.pending)) return;
      try {
        if (autosave) await autosave.flush();
      } catch {
        /* autosave error is surfaced via the status indicator */
      }
      if (nav.kind === 'link') {
        navigate(nav.to);
      } else {
        await nav.onClick();
      }
    },
    [autosave, navigate],
  );

  return (
    <Shell>
      {/* Progress */}
      <header className="mb-10">
        <div className="mb-4 flex items-center justify-between gap-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
            {title}
          </p>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-night-200">
            Progress:{' '}
            <span className="text-night-50">{Math.round(progressPct)}%</span>
          </p>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-night-500/30">
          <div
            className="h-full bg-beam shadow-[0_0_15px_rgba(0,229,255,0.6)] transition-all duration-700 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="mt-6 flex justify-between">
          {STEP_DEFS.map((s) => (
            <StepMarker key={s.n} step={s} current={currentStep} />
          ))}
        </div>
      </header>

      {/* Form container */}
      <div className="relative overflow-hidden rounded-2xl border border-night-500/30 bg-night-800/70 shadow-2xl backdrop-blur-md">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-beam to-transparent opacity-30"
        />

        <div className="p-8 sm:p-12">
          <div className="mb-8">
            <h2 className="mb-2 font-display text-3xl font-extrabold tracking-tight text-beam sm:text-4xl">
              {heading}
            </h2>
            <p className="max-w-xl text-night-200">{subtitle}</p>
          </div>

          {children}
        </div>

        {/* Action bar */}
        <div className="flex flex-col gap-3 border-t border-night-500/20 bg-night-900/40 px-8 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-12">
          {prev ? (
            <NavButton
              variant="ghost"
              nav={prev}
              onClick={() => handleNav(prev)}
            />
          ) : (
            <span aria-hidden="true" className="hidden sm:block" />
          )}

          <div className="flex items-center gap-4">
            {autosave && <AutosaveIndicator status={autosave.status} />}
            {next ? (
              <NavButton
                variant="primary"
                nav={next}
                onClick={() => handleNav(next)}
              />
            ) : null}
          </div>
        </div>
      </div>

      {hint && (
        <p className="mt-4 text-center text-xs text-night-300">{hint}</p>
      )}

      {extras && <div className="mt-8">{extras}</div>}
    </Shell>
  );
}

/* ================================================================== *
 * Shell + status helpers
 * ================================================================== */

function Shell({ children }: { children: ReactNode }) {
  return (
    <section className="mx-auto w-full max-w-4xl px-4 pb-20 pt-8 sm:px-6 sm:pt-12">
      <div className="mb-8 flex items-center justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-night-200">
          Qari Application Portal
        </p>
        <Link
          to="/qari"
          className="rounded-full border border-beam/40 px-5 py-1.5 text-xs font-semibold uppercase tracking-wide text-beam transition-colors hover:bg-beam/10"
        >
          Exit Flow
        </Link>
      </div>
      {children}
    </section>
  );
}

export function LoadingShell({ label = 'Loading…' }: { label?: string }) {
  return (
    <Shell>
      <p className="text-sm text-night-300">{label}</p>
    </Shell>
  );
}

export function ErrorShell({
  title,
  detail,
  onRetry,
}: {
  title: string;
  detail: string;
  onRetry: () => void;
}) {
  return (
    <Shell>
      <div className="rounded-2xl border border-red-400/30 bg-red-400/5 p-8 text-center">
        <h2 className="font-display text-xl font-bold text-night-50">{title}</h2>
        <p className="mt-2 text-sm text-night-300">{detail}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex h-10 items-center justify-center rounded-xl bg-beam px-6 text-xs font-semibold uppercase tracking-wide text-beam-ink transition-transform hover:scale-105 active:scale-95"
        >
          Try again
        </button>
      </div>
    </Shell>
  );
}

/**
 * Convenience: page-level boundary that handles loading/error/redirect.
 * Use:
 *   const draft = useQariDraftProfile();
 *   const guard = renderDraftGuard(draft);
 *   if (guard) return guard;
 *   // here `draft` is narrowed to { phase: 'ready', ... }
 */
export function renderDraftGuard(state: QariDraftState): ReactNode | null {
  if (state.phase === 'loading') {
    return <LoadingShell label="Setting up your draft…" />;
  }
  if (state.phase === 'error') {
    return (
      <ErrorShell
        title={state.title}
        detail={state.detail}
        onRetry={state.retry}
      />
    );
  }
  if (state.phase === 'redirect') {
    return <Navigate to="/qari" replace />;
  }
  return null;
}

/* ================================================================== *
 * Step marker
 * ================================================================== */

function StepMarker({
  step,
  current,
}: {
  step: { n: number; label: string };
  current: number;
}) {
  const isDone = step.n < current;
  const isCurrent = step.n === current;
  const active = isDone || isCurrent;
  return (
    <div
      className={cn(
        'flex items-center gap-3 transition-opacity',
        active ? 'opacity-100' : 'opacity-40',
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full border-2 font-display text-sm font-bold transition-colors',
          active
            ? 'border-beam text-beam'
            : 'border-night-500 text-night-300',
          isCurrent && 'shadow-[0_0_15px_rgba(0,229,255,0.35)]',
        )}
      >
        {isDone ? (
          <IconCheck className="h-4 w-4" />
        ) : (
          String(step.n).padStart(2, '0')
        )}
      </span>
      <span
        className={cn(
          'text-[11px] font-semibold uppercase tracking-wide',
          isCurrent
            ? 'text-beam'
            : active
              ? 'text-night-100'
              : 'text-night-300',
        )}
      >
        {step.label}
      </span>
    </div>
  );
}

/* ================================================================== *
 * Autosave indicator
 * ================================================================== */

export function AutosaveIndicator({ status }: { status: AutosaveStatus }) {
  if (status === 'idle') return null;
  if (status === 'unsaved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-night-300">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        Unsaved
      </span>
    );
  }
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-night-300">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-beam" />
        Saving…
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-beam">
        <IconCheck className="h-3.5 w-3.5" />
        Saved
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-red-300">
      <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
      Save failed
    </span>
  );
}

/* ================================================================== *
 * NavButton
 * ================================================================== */

function NavButton({
  nav,
  variant,
  onClick,
}: {
  nav: WizardNav;
  variant: 'primary' | 'ghost';
  onClick: () => void;
}) {
  if (!nav) return null;
  const isAction = nav.kind === 'action';
  const pending = isAction && nav.pending;
  const disabled = isAction && (nav.disabled || nav.pending);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 rounded-xl px-7 py-2.5 text-xs font-semibold uppercase tracking-wide transition-all',
        variant === 'primary'
          ? 'bg-beam text-beam-ink hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.55)]'
          : 'text-night-200 hover:text-beam-pale',
        disabled && 'opacity-50 cursor-not-allowed hover:shadow-none',
      )}
    >
      {variant === 'ghost' && (
        <IconChevronRight className="h-4 w-4 rotate-180" />
      )}
      {pending ? 'Working…' : nav.label}
      {variant === 'primary' && !pending && (
        <IconChevronRight className="h-4 w-4" />
      )}
    </button>
  );
}

/* ================================================================== *
 * Form-field primitives
 * ================================================================== */

const FIELD_CLS = cn(
  'w-full rounded-t-lg border-b-2 border-night-500/50 bg-night-900/60',
  'px-4 py-3 text-sm text-night-50 placeholder:text-night-300',
  'outline-none transition-all',
  'focus:border-beam focus:bg-night-900',
);

export function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-2 flex items-end justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-beam">
          {label}
          {required && <span className="text-beam-soft"> *</span>}
        </span>
        {hint && (
          <span className="text-[11px] text-night-300">{hint}</span>
        )}
      </div>
      {children}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className={FIELD_CLS}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={cn(FIELD_CLS, 'resize-none')}
    />
  );
}

export function SelectInput({
  value,
  onChange,
  onChangeRaw,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Escape hatch for components that need the raw event. */
  onChangeRaw?: (e: ChangeEvent<HTMLSelectElement>) => void;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) =>
          onChangeRaw ? onChangeRaw(e) : onChange(e.target.value)
        }
        className={cn(FIELD_CLS, 'cursor-pointer appearance-none pr-10')}
      >
        {children}
      </select>
      <IconChevronRight className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-night-300" />
    </div>
  );
}

/* ================================================================== *
 * Text helpers
 * ================================================================== */

/** Empty/whitespace string → null; otherwise trim. */
export function nullableText(s: string | null | undefined): string | null {
  if (s === null || s === undefined) return null;
  const trimmed = s.trim();
  return trimmed === '' ? null : trimmed;
}
